import React, {useEffect, useState} from 'react'
import {Box, Button, Flex, Stack, Text} from '../../../vanilla'
import {DEFAULT_PRODUCTS_AMOUNT_TO_LOAD} from '../../../utils/constants'
import {Hit} from '../../../store/ProductStore'
import {Modal} from '../../modal'
import {ModalNavigationProps} from '../../../types/ui'
import {PaymentLoaderIcon} from '../../icons'
import {ProductTileCompact, ProductTileCompactSkeleton} from '../../product-tile-compact'
import {Slot} from '../../cms/Slot'
import {detectBrowser} from '../../../utils/utils'
import {getPromoExpiryText} from '../../../store/PromotionStore'
import {loadingIcon} from '../../payment-processing-overlay/style.css'
import {observer} from 'mobx-react'
import {promotionModalContent} from './styles.css'
import {transformHit} from '../../algolia/util/transformHit'
import {
  useContentStore,
  useGlobalStore,
  useProductStore,
  useRapidDeliveryStore
} from '../../../store/hooks/useStore'
import {usePromotion} from '../../../store/hooks/usePromotion'
import {useLocation} from 'react-router-dom'
import {useViewItemListTracking} from '../../../analytics/view-item-list-tracking'
import {
  Configure,
  InstantSearch as AlgoliaInstantSearch,
  useInstantSearch
} from 'react-instantsearch-hooks-web'
import {LocationState} from '../../../types/forms'
import {NormalizedPromotion} from '../../../store/PromotionStore'

export const PromotionModal = observer(() => {
  const {
    algoliaIndexBase,
    selectedAvailabilityDayOffset,
    selectedStoreId,
    searchClient,
    customSitePreferences,
    PromoModalOpen,
    PromoModalPromo,
    setIsPromoModalOpen,
    isRegionalIndexABTestEnabled
  } = useGlobalStore()

  const {pathname} = useLocation()
  const promo = usePromotion(PromoModalPromo?.ID)

  const [searchConfigPage, setSearchConfigPage] = useState(0)

  useEffect(() => {
    if (PromoModalOpen) {
      setIsPromoModalOpen(false)
    }
  }, [pathname])

  const hitsPerPage =
    typeof customSitePreferences['promoModalPageSize'] === 'number'
      ? customSitePreferences['promoModalPageSize']
      : DEFAULT_PRODUCTS_AMOUNT_TO_LOAD

  const cms = useContentStore()

  useEffect(() => {
    (async function () {
      if (promo?.c_contentAsset) {
        await cms.fetchItemByKey(promo.c_contentAsset)
      }
    })()
    setSearchConfigPage(0) // Reset to page 0
  }, [promo])

  const indexName = `${algoliaIndexBase}__products__default`

  const [promotionalModalProductIds, setPromotionalModalProductIds] = useState<
    Record<string, string[]>
  >({})
  const [safariBrowser, setSafariBrowser] = useState(false)
  const promotionProductIds = promo?.id ? promotionalModalProductIds[promo.id] ?? [] : []

  useEffect(() => {
    if (!PromoModalOpen) {
      setSearchConfigPage(0)
    }
  }, [PromoModalOpen])

  const availabilityFilter = `matrixAvailability.${selectedAvailabilityDayOffset}:${selectedStoreId}`
  const inStockFilter = `in_stock:true`

  const modalProps = {
    isOpen: PromoModalOpen,
    onClose: () => setIsPromoModalOpen(false),
    onToggle: () => (PromoModalOpen ? setIsPromoModalOpen(false) : setIsPromoModalOpen(true))
  }

  useEffect(() => {
    const browser = detectBrowser()
    if (browser == 'Safari') {
      setSafariBrowser(true)
    }
  }, [])

  return (
    <Modal
      title="Product promotions"
      size="promotions"
      footerCloseBtnText="Continue shopping"
      dialogContentProps={{
        position: 'relative'
      }}
      {...modalProps}
    >
      <AlgoliaInstantSearch key={promo?.id} searchClient={searchClient} indexName={indexName}>
        {/** @TODO This is ignored from TS checking here because it requires an Algolia rewrite, which is in future plans. Remove the ignore when this happens
         * @ts-ignore **/}
        <Configure
          // @ts-ignore
          clickAnalytics
          filters={`productPromotions.nearPromos.ID:${promo?.id} AND ${inStockFilter} AND NOT ${availabilityFilter}`}
          hitsPerPage={hitsPerPage}
          page={searchConfigPage}
          {...(isRegionalIndexABTestEnabled === false && {enableABTest: false})}
        />
        <Stack
          spacing="28px"
          backgroundColor="gray50"
          className={promotionModalContent}
          style={safariBrowser ? {height: '500px'} : {}}
        >
          <Stack spacing="12px" paddingTop="16px" paddingX={['16px', '28px']}>
            {promo?.c_contentAsset && cms.contentById[promo.c_contentAsset] && (
              <Slot deliveryKey={promo.c_contentAsset} />
            )}
            {PromoModalPromo?.ladderDeals && (
              <Text variant="text6">
                {PromoModalPromo.ladderDeals.map((deal) => deal.msg).join(' or ')}
              </Text>
            )}
            <Text variant="text6">{promo?.calloutMsg}</Text>
            {promo?.details && <Text variant="text3">{promo.details}</Text>}
            <Text variant="text1">{getPromoExpiryText(promo?.startDate, promo?.endDate)}</Text>
          </Stack>
          <PromotionProducts
            setNewPage={(newOffsetNb: number) => setSearchConfigPage(newOffsetNb)}
            productIds={promotionProductIds}
            hitsPerPage={hitsPerPage}
            setPromotionalModalProductIds={setPromotionalModalProductIds}
            onToggle={modalProps.onToggle}
            promoModalOpen={PromoModalOpen}
            promo={promo}
          />
        </Stack>
      </AlgoliaInstantSearch>
    </Modal>
  )
})

interface PromotionProductsProps {
  setNewPage: (newOffsetNb: number) => void
  productIds: string[]
  hitsPerPage: number
  setPromotionalModalProductIds: React.Dispatch<React.SetStateAction<Record<string, string[]>>>
  onToggle: () => void
  modalNavigationProps?: ModalNavigationProps
  promoModalOpen: boolean
  promo: NormalizedPromotion | null
}

const PromotionProducts = observer(
  ({
    setNewPage,
    productIds,
    hitsPerPage,
    setPromotionalModalProductIds,
    onToggle,
    promoModalOpen,
    promo
  }: PromotionProductsProps) => {
    const {status, results, refresh} = useInstantSearch()
    const {storeHitsAsProducts} = useProductStore()
    const {selectedStoreId, selectedPriceDayOffset, selectedRegion} = useGlobalStore()
    const {isRapidPricingActive} = useRapidDeliveryStore()
    // Local state, so it resets to one page each time the modal opens
    const [visibleCount, setVisibleCount] = useState(hitsPerPage)
    const isLoading = status === 'loading'
    const showInitialLoadSkeletons = results?.hits?.length === 0 && isLoading
    const location = useLocation<LocationState>()
    const redirectedFromLogin = location?.state?.directedFrom === '/login'
    const isPromoModal = true
    const sendViewItemListData = useViewItemListTracking()

    const nextPageToFetch = Math.ceil(productIds.length / hitsPerPage)
    const hasMoreFetched = visibleCount < productIds.length
    const hasMoreToFetch = results?.nbPages ? nextPageToFetch < results.nbPages : false

    useEffect(() => {
      if (promoModalOpen && results?.hits?.length) {
        const viewItemListData = {items: results.hits, promo, isPromoModal}
        sendViewItemListData(viewItemListData)
      }
    }, [promoModalOpen, results?.queryID])

    const handleLoadMore = () => {
      if (!hasMoreFetched && hasMoreToFetch) {
        setNewPage(nextPageToFetch)
      }
      setVisibleCount((count) => count + hitsPerPage)
    }

    useEffect(() => {
      if (results?.hits?.length) {
        const transformedHits = results?.hits.map((hit) =>
          transformHit(
            hit,
            selectedPriceDayOffset,
            selectedStoreId,
            selectedRegion,
            isRapidPricingActive
          )
        ) as Hit[]
        const hitIds = transformedHits?.map((hit) => hit.id) ?? []
        // Push the hits returned from Algolia into the Product Store to save ProductStore refetching those hits from SSR
        storeHitsAsProducts(transformedHits)
        // Feed the list of products in this update to the list of products to render in this component
        if (promo?.id) {
          setPromotionalModalProductIds((previous) => {
            const previousIds = previous[promo.id] ?? []
            const nextIds = redirectedFromLogin ? hitIds : [...new Set([...previousIds, ...hitIds])]
            return {...previous, [promo.id]: nextIds}
          })
        }
      }
    }, [results?.hits, promo?.id, redirectedFromLogin])

    useEffect(() => {
      if (redirectedFromLogin) {
        refresh()
      }
    }, [])

    return (
      <>
        {isLoading && (
          <Flex
            position="absolute"
            top="0px"
            left="0px"
            width="full"
            height="full"
            align="center"
            justify="center"
            zIndex="overlay"
            style={{backgroundColor: 'rgba(255, 255, 255, 0.6)'}}
          >
            <PaymentLoaderIcon
              style={{opacity: 0.7}}
              width="72px"
              height="72px"
              className={loadingIcon}
            />
          </Flex>
        )}
        <Box
          display="grid"
          gridTemplateColumns={['1x', '2x']}
          paddingBottom="16px"
          paddingX={['0px', '28px']}
          style={{
            gap: '32px 16px'
          }}
        >
          {showInitialLoadSkeletons
            ? Array.from({length: 5}).map((_, index) => (
                <ProductTileCompactSkeleton key={`skeleton-promo-product-tile-${index}`} />
              ))
            : null}
          {productIds.slice(0, visibleCount).map((id, index) => (
            <ProductTileCompact
              key={`promo-product-tile-${id}-${index}`}
              productId={id}
              onToggle={onToggle}
              hitItemIndex={index}
            />
          ))}
        </Box>
        <Flex justify="center">
          {(hasMoreFetched || hasMoreToFetch) && (
            <Button
              variant="dark"
              width="full"
              size="sm"
              onClick={handleLoadMore}
              disabled={isLoading}
              style={{maxWidth: 230}}
              marginBottom={['40px', '60px']}
            >
              Load more
            </Button>
          )}
        </Flex>
      </>
    )
  }
)
